/**
 * Landing section — "Esercizio" feature (Move): guided micro-workouts
 * for everyone + motivational music player. Self-contained translations
 * to keep Landing.js lean.
 */
import { Dumbbell, Play, SkipBack, SkipForward, Heart, Star, Music, ChevronRight } from 'lucide-react';

const T = {
  it: {
    title: 'Esercizio',
    subtitle: 'movimento alla portata di tutti',
    description: 'Allenamenti guidati da 5 a 12 minuti, pensati per chi parte da zero: esercizi personalizzati e semplici per fare movimento ogni giorno e favorire il dimagrimento. Con musica motivazionale integrata.',
    bullets: ['Workout automatici: premi Inizia e segui', 'Esercizi adatti a tutti, senza attrezzi', 'Player musicale motivazionale (Energia, Cardio, Focus)'],
    todayLabel: 'Esercizio di oggi', progress: 'Esercizio 1 di 7',
    exName: 'Shadow Boxing', exDesc: 'Cardio divertente che scioglie spalle e braccia',
    next: 'SUCCESSIVO', moods: ['Energia', 'Cardio', 'Focus'], track: 'Motivation Mix — Slimmy',
  },
  en: {
    title: 'Exercise',
    subtitle: 'movement for everyone',
    description: 'Guided 5–12 minute workouts designed for absolute beginners: simple, personalized exercises to move every day and support weight loss. With built-in motivational music.',
    bullets: ['Automatic workouts: press Start and follow', 'Exercises for everyone, no equipment', 'Motivational music player (Energy, Cardio, Focus)'],
    todayLabel: "Today's Exercise", progress: 'Exercise 1 of 7',
    exName: 'Shadow Boxing', exDesc: 'Fun cardio that loosens shoulders and arms',
    next: 'NEXT', moods: ['Energy', 'Cardio', 'Focus'], track: 'Motivation Mix — Slimmy',
  },
  es: {
    title: 'Ejercicio',
    subtitle: 'movimiento al alcance de todos',
    description: 'Entrenamientos guiados de 5 a 12 minutos, pensados para quien empieza de cero: ejercicios sencillos y personalizados para moverte cada día y favorecer la pérdida de peso. Con música motivacional integrada.',
    bullets: ['Entrenamientos automáticos: pulsa Empezar y sigue', 'Ejercicios para todos, sin material', 'Reproductor de música motivacional (Energía, Cardio, Focus)'],
    todayLabel: 'Ejercicio de hoy', progress: 'Ejercicio 1 de 7',
    exName: 'Shadow Boxing', exDesc: 'Cardio divertido que suelta hombros y brazos',
    next: 'SIGUIENTE', moods: ['Energía', 'Cardio', 'Focus'], track: 'Motivation Mix — Slimmy',
  },
  pt: {
    title: 'Exercício',
    subtitle: 'movimento ao alcance de todos',
    description: 'Treinos guiados de 5 a 12 minutos, pensados para quem começa do zero: exercícios simples e personalizados para se mexer todos os dias e favorecer o emagrecimento. Com música motivacional integrada.',
    bullets: ['Treinos automáticos: toque em Começar e siga', 'Exercícios para todos, sem equipamento', 'Leitor de música motivacional (Energia, Cardio, Focus)'],
    todayLabel: 'Exercício de hoje', progress: 'Exercício 1 de 7',
    exName: 'Shadow Boxing', exDesc: 'Cardio divertido que solta ombros e braços',
    next: 'SEGUINTE', moods: ['Energia', 'Cardio', 'Focus'], track: 'Motivation Mix — Slimmy',
  },
  de: {
    title: 'Übung',
    subtitle: 'Bewegung für alle',
    description: 'Geführte Workouts von 5 bis 12 Minuten, gemacht für Einsteiger: einfache, personalisierte Übungen, um sich jeden Tag zu bewegen und das Abnehmen zu unterstützen. Mit integrierter Motivationsmusik.',
    bullets: ['Automatische Workouts: Start drücken und mitmachen', 'Übungen für alle, ohne Geräte', 'Motivations-Musikplayer (Energie, Cardio, Fokus)'],
    todayLabel: 'Übung des Tages', progress: 'Übung 1 von 7',
    exName: 'Schattenboxen', exDesc: 'Spaßiges Cardio für lockere Schultern und Arme',
    next: 'WEITER', moods: ['Energie', 'Cardio', 'Fokus'], track: 'Motivation Mix — Slimmy',
  },
  fr: {
    title: 'Exercice',
    subtitle: 'du mouvement pour tous',
    description: "Des entraînements guidés de 5 à 12 minutes, pensés pour les débutants : des exercices simples et personnalisés pour bouger chaque jour et favoriser la perte de poids. Avec musique motivante intégrée.",
    bullets: ["Entraînements automatiques : appuyez sur Commencer et suivez", 'Exercices pour tous, sans matériel', 'Lecteur de musique motivante (Énergie, Cardio, Focus)'],
    todayLabel: "Exercice du jour", progress: 'Exercice 1 sur 7',
    exName: 'Boxe dans le vide', exDesc: 'Cardio amusant qui détend épaules et bras',
    next: 'SUIVANT', moods: ['Énergie', 'Cardio', 'Focus'], track: 'Motivation Mix — Slimmy',
  },
};

export default function LandingExerciseSection({ lang }) {
  const t = T[lang] || T.en;
  return (
    <section className="relative py-20 px-4 overflow-hidden" data-testid="landing-exercise-section">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-[#E8334A]/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#F59E0B]/10 rounded-full blur-[100px]" />
      </div>

      <div className="max-w-5xl mx-auto relative z-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#E8334A]/15 text-[#F87171] text-xs font-bold uppercase tracking-wide mb-4">
            <Dumbbell className="w-3.5 h-3.5" /> NEW
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-2">
            {t.title}
            <span className="block bg-gradient-to-r from-[#E8334A] to-[#F59E0B] bg-clip-text text-transparent">
              {t.subtitle}
            </span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto mt-4">{t.description}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-10 items-center">
          {/* Bullets */}
          <div className="space-y-4 order-2 md:order-1">
            {t.bullets.map((b, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ background: 'linear-gradient(135deg, #E8334A 0%, #F59E0B 100%)' }}>
                  <ChevronRight className="w-3.5 h-3.5 text-white" />
                </div>
                <p className="text-gray-300">{b}</p>
              </div>
            ))}
          </div>

          {/* Player mock */}
          <div className="order-1 md:order-2 max-w-sm w-full mx-auto">
            <div className="rounded-3xl bg-[#111827] border border-white/10 p-4 shadow-2xl">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-white font-bold text-sm">{t.todayLabel}</p>
                  <p className="text-gray-500 text-xs">{t.progress}</p>
                </div>
                <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center text-gray-400 text-xs">✕</div>
              </div>
              <div className="flex gap-1.5 mb-2">
                {t.moods.map((m, i) => (
                  <span key={m}
                    className={`px-3 py-1 rounded-full text-[11px] font-semibold ${i === 0 ? 'text-white' : 'bg-white/5 text-gray-400'}`}
                    style={i === 0 ? { background: 'linear-gradient(135deg, #E8334A 0%, #F59E0B 100%)' } : {}}>
                    {m}
                  </span>
                ))}
              </div>
              <div className="px-2.5 py-1.5 rounded-xl bg-white/5 border border-white/10 flex items-center gap-1.5 mb-3">
                <Music className="w-3.5 h-3.5 text-gray-500" />
                <p className="flex-1 text-gray-300 text-[11px] truncate">{t.track}</p>
                <Heart className="w-3.5 h-3.5 text-[#E8334A] fill-[#E8334A]" />
                <Star className="w-3.5 h-3.5 text-gray-500" />
                <SkipBack className="w-3.5 h-3.5 text-gray-400" />
                <span className="w-6 h-6 rounded-full flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, #E8334A 0%, #F59E0B 100%)' }}>
                  <Play className="w-3 h-3 text-white ml-0.5" />
                </span>
                <SkipForward className="w-3.5 h-3.5 text-gray-400" />
              </div>
              <div className="rounded-2xl bg-white overflow-hidden mb-3">
                <img src="/landing/exercise-mock.jpg" alt={t.exName} className="w-full h-auto object-cover" data-testid="landing-exercise-image" />
              </div>
              <p className="text-white font-bold text-lg">{t.exName}</p>
              <p className="text-gray-400 text-xs mb-2">{t.exDesc}</p>
              <p className="text-white text-center font-bold text-4xl mb-3">0:40</p>
              <div className="w-full py-3 rounded-2xl text-center text-white font-bold text-sm flex items-center justify-center gap-1.5"
                style={{ background: 'linear-gradient(135deg, #E8334A 0%, #F59E0B 100%)' }}>
                {t.next} <ChevronRight className="w-4 h-4" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
